/**
 * Shared dispatch types used by the shell, day swimlane, route rail, and
 * capacity strip. Centralised so we only describe the wire shapes once.
 */

import { customerWoNumber } from "@/lib/wo-number";

export interface DispatchJob {
    id: string;
    title: string;
    status: string;
    customer_name: string | null;
    assigned_to: string | null;
    assigned_to_name: string | null;
    /** Flexible crew: non-primary crew members (job_assignments). Multi-tech
     *  jobs render on every member's lane with a crew chip. */
    crew?: Array<{ user_id: string; name: string | null; role: string; earning_mode?: string }> | null;
    /** Return Needed → Back to Awaiting Dispatch: the return trip's story
     *  (reason, remaining devices, original visit, due-by). */
    return_context?: {
        via?: string;
        reasons?: string[];
        reason_text?: string | null;
        remaining_devices?: Array<{ asset_id: string; label: string; serial: string | null }>;
        original_visit?: { scheduled_start?: string | null; tech_name?: string | null };
        due_by_date?: string | null;
        returned_at?: string;
    } | null;
    trade_name: string | null;
    trade_code: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    /** #1465 step 4 — the placement stamp (migration 590). A drop sends it
     *  back as `ifDispatchChangedAt`; the server refuses the move if it moved. */
    dispatch_changed_at?: string | null;
    /** Job lifecycle timestamps — drive Ops Center "last activity" age
     *  derivation (no live GPS). Set as the tech advances the job. */
    en_route_at?: string | null;
    arrived_at?: string | null;
    work_started_at?: string | null;
    completed_at?: string | null;
    notes?: string | null;
    /** The office's on-site brief (jobs.summary). On a job made from a quote:
     *  one plain line per device — shown in the Grid hover card. */
    summary?: string | null;
    /** Optional planning hint surfaced by /api/jobs (job type duration). */
    estimated_duration_minutes?: number | null;
    /** Cached productive points for the job (points-capacity model). */
    computed_points?: number | string | null;
    /** G3 trust payload: the calculator's reasoning for the points +
     *  duration ("2 devices × repair 15 = 30 pts" / "3h estimate labor →
     *  180 min"). Rendered as the breakdown tooltip on the pts chip and
     *  job block. */
    points_breakdown?: {
        points_explanation?: string | null;
        duration_explanation?: string | null;
        job_intent?: "test" | "repair" | null;
        asset_count?: number | null;
    } | null;
    /** Optional site coords used for map rail / clustering. */
    latitude?: number | null;
    longitude?: number | null;
    job_type_id?: string | null;
    job_type_code?: string | null;
    /** Site / location name (e.g. "Las Colinas HQ"). */
    site_label?: string | null;
    /** The site's own name (sites.name, e.g. "7-Eleven #32379") from /api/jobs.
     *  The new Grid's card title (#1766). */
    site_name?: string | null;
    /** Site city + state, used by restricted-city eligibility checks. */
    site_city?: string | null;
    site_region?: string | null;
    /** Site street line + ZIP. The grid card renders the full mailing address
     *  (street, city/region, ZIP) — Corbin: cards must carry the whole
     *  address, not just the city. */
    site_address_line?: string | null;
    site_postal_code?: string | null;
    /** Water provider / jurisdiction name for the site (e.g. "City of Plano").
     *  Rendered as a jurisdiction chip on the grid. */
    water_provider_name?: string | null;
    /** Water provider id — drives the V3 jurisdiction-registration warning
     *  when assigning a lead who isn't registered with a strict provider. */
    water_provider_id?: string | null;
    /** Human-readable job type (e.g. "Backflow Test"). */
    job_type_name?: string | null;
    /** 'unlocked' = the auto-scheduler may move this job. 'confirmed' = the
     *  customer has been notified, the algorithm must NOT touch it (manual
     *  edits remain allowed). Drives the lock chip on the day swimlane. */
    dispatch_lock_state?: "unlocked" | "confirmed" | null;
    confirmed_at?: string | null;
    /** Number of linked assets on this job (Level 2 visibility). */
    asset_count?: number | null;
    /** Comma-separated distinct assembly types (e.g. "DCVA, RPZ"). */
    asset_types?: string | null;
    /** Customer priority tier (T1 highest). Drives the tier badge. */
    priority_tier?: "T1" | "T2" | "T3" | null;
    /** Customer payment profile — card-required profiles gate dispatch. */
    payment_profile?: string | null;
    /** True when the customer has a saved card on file. Card-required
     *  customers without one can't be dispatched (pre-dispatch gate). */
    has_card_on_file?: boolean | null;
    /** Hard "not before" floor (YYYY-MM-DD) — parts delivery + 2 days.
     *  Rendered as a "not before" chip on the demand panel. */
    earliest_dispatch_date?: string | null;
    /** True when a linked PO's parts haven't been received yet — the job
     *  can't be dispatched until received (parts gate). */
    parts_blocked?: boolean | null;
    /** Jurisdiction fail protocol paused on this job — water is off, waiting on the customer's NTE. */
    fail_protocol_paused?: boolean | null;
    /** The Quiet Toolbar (10/10) — mark state, see lib/job-marks.ts. */
    from_online_booking?: boolean | null;
    tech_flag_reason?: string | null;
    /** "No work order yet" (2026-10-21) — a work-order customer's job with none on it. */
    wo_missing?: boolean | null;
    fail_protocol_cleared?: boolean | null;
    helper_results_to_review?: number | null;
    /** Hard compliance deadline (YYYY-MM-DD). Drives the red/amber due pill
     *  on dispatch job cards (see lib/dueUrgency). */
    due_by_date?: string | null;
    /** Count of open/in-progress office-review items on this job. When > 0
     *  while the job is still in_progress, the tech has submitted tests that
     *  are awaiting office review ("Tests Submitted" state in Ops Center). */
    active_office_review_count?: number | null;
    /** When the site's default contact was last confirmed current (ISO).
     *  Null = never verified. Drives the amber stale-contact chip. */
    site_contact_last_verified_at?: string | null;
    // ── Vitals row (field directive 2026-07-14, unit 1) ──────────────────
    /** Customer PO on the job — rendered "PO …" on the vitals row. */
    customer_po?: string | null;
    /** Vendor WO number (external_ref, briefing fallback) — rendered "WO …". */
    external_ref?: string | null;
    /** WO number from work_order_briefing when external_ref is empty. */
    briefing_wo_number?: string | null;
    /** Location contact phone: site phone, falling back to customer phone. */
    site_phone?: string | null;
    customer_phone?: string | null;
    /** Water provider account # for the site (sites.water_account_number). */
    site_water_account_number?: string | null;
    /** True when the provider files via a portal (reporting_platforms.is_portal).
     *  Portal + no account # = the amber MISSING state — that amber IS the point. */
    water_provider_is_portal?: boolean | null;
}

// ── Vitals row helpers (pure, unit-tested) ─────────────────────────────────

export type VitalsAcct =
    | { kind: "value"; value: string }
    | { kind: "paper" } // provider files on paper/email — no account expected
    | { kind: "missing" } // portal city with NO account — the amber alert state
    | { kind: "none" }; // no provider on the site — show nothing

export interface JobVitals {
    /** "PO 4500123" / "WO S2604240620" — PO wins when both exist; both shown at full width. */
    refs: string[];
    phone: string | null;
    acct: VitalsAcct;
}

export function jobVitals(job: {
    customer_po?: string | null;
    external_ref?: string | null;
    briefing_wo_number?: string | null;
    site_phone?: string | null;
    customer_phone?: string | null;
    site_water_account_number?: string | null;
    water_provider_is_portal?: boolean | null;
    water_provider_name?: string | null;
    water_provider_id?: string | null;
}): JobVitals {
    const refs: string[] = [];
    const po = job.customer_po?.trim();
    if (po) refs.push(`PO ${po}`);
    const wo = customerWoNumber(job, { includePo: false });
    if (wo) refs.push(`WO ${wo}`);
    const phone = job.site_phone?.trim() || job.customer_phone?.trim() || null;
    const acctNo = job.site_water_account_number?.trim();
    const hasProvider = !!(job.water_provider_id || job.water_provider_name);
    // Portal-first: a paper/offline jurisdiction has no portal account, so a
    // stored number is stale/legacy — suppress it and read "paper". Only
    // portals surface the account value (or the amber "missing" nudge).
    const acct: VitalsAcct = !hasProvider
        ? { kind: "none" }
        : !job.water_provider_is_portal
          ? { kind: "paper" }
          : acctNo
            ? { kind: "value", value: acctNo }
            : { kind: "missing" };
    return { refs, phone, acct };
}

/**
 * G3 width honesty for the vitals row: cards never pretend. At the
 * smallest widths only the WO/PO reference + account survive; the phone
 * joins at full width (it's always in the hover tooltip / expanded view).
 */
export function vitalsVisible(widthPx: number): { refs: boolean; phone: boolean; acct: boolean } {
    if (widthPx < 90) return { refs: false, phone: false, acct: false };
    if (widthPx < 200) return { refs: true, phone: false, acct: true };
    return { refs: true, phone: true, acct: true };
}

// ─── Full site address (grid + backlog cards) ──────────────────────────────

/**
 * One-line full mailing address for dispatch job cards: "401 East Mayfield
 * Road, Arlington, TX 76010". Corbin's rule — cards show the WHOLE address
 * including ZIP, not just the street. Blank pieces are skipped so a site
 * missing a region/ZIP never renders a dangling comma. Returns null when
 * there's nothing to show.
 */
export function formatJobAddress(parts: {
    addressLine?: string | null;
    city?: string | null;
    region?: string | null;
    postalCode?: string | null;
}): string | null {
    const street = parts.addressLine?.trim() || "";
    const city = parts.city?.trim() || "";
    const region = parts.region?.trim() || "";
    const zip = parts.postalCode?.trim() || "";
    const cityRegion = [city, region].filter(Boolean).join(", ");
    const locality = [cityRegion, zip].filter(Boolean).join(" ");
    const out = [street, locality].filter(Boolean).join(", ");
    return out.length ? out : null;
}

export interface DispatchTech {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    /** Optional capacity in minutes per day (defaults at the UI layer). */
    daily_capacity_minutes?: number | null;
    /** Points-capacity model: soft daily target / hard cap / weekly floor. */
    daily_points_target?: number | null;
    daily_points_cap?: number | null;
    weekly_points_target?: number | null;
    /** #828 M9 — the roster endpoint already returns this; typed here so the
     *  "(helper)" hint in pickers doesn't have to guess. */
    is_field_helper?: boolean | null;
}

export interface Trade {
    id: string;
    name: string;
    code: string;
}

export interface TechRouteStop {
    jobId: string;
    title: string;
    status: string;
    /** Null when the site has no geocode — the stop is still listed but
     *  cannot be drawn on the route map. */
    latitude: number | null;
    longitude: number | null;
    /** True when the job has no site coordinates and should be rendered as
     *  an "unmapped" pill in the route panel rather than a map marker. */
    unmapped?: boolean;
    scheduled_start: string | null;
    customer_name: string;
    site_name: string;
}

export interface TechRoute {
    techId: string;
    techName: string;
    jobs: TechRouteStop[];
}

export interface ShopLocation {
    lat: number;
    lng: number;
    label: string;
}

/** Trade -> CSS background color (Tailwind shades). */
export const TRADE_BG: Record<string, string> = {
    backflow: "bg-blue-500",
    fire_ext: "bg-red-500",
    underground_fire: "bg-orange-500",
    plumbing: "bg-green-500",
    hvac: "bg-purple-500",
};

/** Trade -> hex color (used by leaflet). */
export const TRADE_HEX: Record<string, string> = {
    backflow: "#378ADD",
    fire_ext: "#E24B4A",
    underground_fire: "#D85A30",
    plumbing: "#639922",
    hvac: "#7F77DD",
};

export const ROUTE_HEX: string[] = [
    "#3b82f6",
    "#ef4444",
    "#22c55e",
    "#f97316",
    "#a855f7",
    "#06b6d4",
    "#ec4899",
    "#84cc16",
    "#f59e0b",
    "#6366f1",
];

/** Default minutes per visit when a job has no end time / no estimate. */
/**
 * Statuses that mean the job is finished with — done or called off. Terminal
 * work is never waiting on a dispatcher, so it must not appear in any
 * "unassigned / needs attention" surface no matter what else is true of it.
 */
export const TERMINAL_JOB_STATUSES = new Set(["completed", "cancelled"]);

export function isTerminalJob(job: { status: string }): boolean {
    return TERMINAL_JOB_STATUSES.has(job.status);
}

/**
 * A job the dispatcher still has to place: not terminal, and missing a tech.
 *
 * Issue #1214 — the Grid's "Unassigned Today" box tested `!assigned_to` alone,
 * so completed-but-never-assigned jobs poured into an amber alarm meant for
 * work nobody had picked up. Archive imports (completed, unassigned, dated)
 * made it visible at 481 rows in a day, but a real job a tech closed out
 * without ever being assigned in the app hit it too. Both callers go through
 * here now so the two can't drift apart again.
 */
export function awaitsDispatch(job: { status: string; assigned_to: string | null }): boolean {
    return !isTerminalJob(job) && !job.assigned_to;
}

export const DEFAULT_VISIT_MINUTES = 60;

/**
 * G3 amendment (Corbin's card rules): 30 minutes is the minimum card AND
 * the smallest window we ever schedule — with the shared understanding
 * that work often finishes faster. Every scheduling path and the grid's
 * block math clamp through this.
 */
export const MIN_VISIT_MINUTES = 30;

/**
 * Visit length for scheduling writes: the manually-set range wins over the
 * modeled estimate, the model over the 60-min default — and everything
 * clamps to the 30-minute floor.
 */
export function visitMinutesFromJob(job: {
    scheduled_start?: string | null;
    scheduled_end?: string | null;
    estimated_duration_minutes?: number | null;
}): number {
    let minutes = job.estimated_duration_minutes ?? DEFAULT_VISIT_MINUTES;
    if (job.scheduled_start && job.scheduled_end) {
        const s = Date.parse(job.scheduled_start);
        const e = Date.parse(job.scheduled_end);
        if (Number.isFinite(s) && Number.isFinite(e) && e > s) {
            minutes = Math.round((e - s) / 60_000);
        }
    }
    return Math.max(MIN_VISIT_MINUTES, minutes);
}

/** Default per-tech capacity for the day in minutes (8 hours). */
export const DEFAULT_DAILY_CAPACITY_MINUTES = 8 * 60;

/**
 * Visible day window for the swimlane. Defaults to business hours (7a–6p) so
 * the board isn't padded with dead early/late hours — the #1 source of the
 * "everything is compressed" feeling (2026-08-01, #587). Org settings
 * (`dispatch.day.start_hour` / `.end_hour`) still override, the full-day
 * toggle widens to 0–24, and jobs outside the window surface as edge chips.
 */
export const DAY_WINDOW_START_HOUR = 7;
export const DAY_WINDOW_END_HOUR = 18;

// ─── Grid card sizing (readable-first, weight by points — #587) ─────────────
//
// Card WIDTH is decoupled from raw duration: a readable floor kills the "S…"
// slivers, a hard cap stops a long repair from sprawling across the board, and
// a gentle linear ramp in between keeps it "roughly honest" (a 3-hour repair is
// still visibly wider than a 30-minute test — G3 doctrine, amended 2026-08-01).
// Duration itself is never lost: every card carries the real minutes in its
// duration chip + tooltip, and the true clock position still drives layout.

export type BoardCardDensity = "comfortable" | "compact";

interface CardWidthBand {
    /** Minimum readable card width in px. */
    floor: number;
    /** Hard cap so a long job never sprawls. */
    cap: number;
    /** px added per scheduled minute (the "roughly honest" ramp). */
    slope: number;
    /** px at zero minutes (floor still wins for tiny jobs). */
    base: number;
}

const CARD_WIDTH_BANDS: Record<BoardCardDensity, CardWidthBand> = {
    comfortable: { floor: 176, cap: 300, slope: 0.55, base: 150 },
    compact: { floor: 128, cap: 210, slope: 0.42, base: 108 },
};

/**
 * Readable, capped, monotonic card width for `minutes` of work at the given
 * board density. Monotonic non-decreasing in minutes; always within
 * `[floor, cap]`. This is the geometry that replaced strict width∝minutes.
 */
export function readableCardWidth(minutes: number, density: BoardCardDensity = "comfortable"): number {
    const band = CARD_WIDTH_BANDS[density];
    const safeMinutes = Number.isFinite(minutes) ? Math.max(0, minutes) : 0;
    return Math.round(clampNum(band.base + safeMinutes * band.slope, band.floor, band.cap));
}

function clampNum(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, n));
}

/**
 * Short, human duration label for a card's duration chip: "45m" under an hour,
 * else whole/half hours ("1h", "1.5h", "3h"). Duration is carried honestly here
 * even though width is capped.
 */
export function cardDurationLabel(minutes: number): string {
    const m = Math.max(0, Math.round(minutes));
    if (m < 60) return `${m}m`;
    const hours = m / 60;
    const rounded = Math.round(hours * 2) / 2; // nearest half hour
    return `${rounded}h`;
}
